import { z } from 'zod';
import { email, isoDate, uuid } from './common.js';

/**
 * A-05 · Document review queue: every certificate and licence waiting for the organiser, among
 * the event's participants (a paid place in it), oldest first. One item per thing to review:
 * a participant with both a certificate and a licence pending appears twice.
 */
const participant = z.object({
  userId: uuid,
  email,
  firstName: z.string().nullable(),
  lastName: z.string().nullable(),
  birthDate: z.string().nullable(),
  /** Paid formats in the event, for context. */
  formats: z.array(z.string()),
  /** One of their paid registrations, for the link to its detail page (A-04). */
  registrationId: uuid,
});

export const adminReviewItem = z.discriminatedUnion('kind', [
  z.object({
    kind: z.literal('certificate'),
    /** The document id: preview with `GET /admin/documents/:id/url`, review with its `/review`. */
    id: uuid,
    submittedAt: isoDate,
    participant,
    certificate: z.object({
      fileName: z.string(),
      contentType: z.string(),
      sizeBytes: z.number().int(),
      /** As declared by the participant; the organiser checks them against the file. */
      issuedOn: z.string().nullable(),
      expiresOn: z.string().nullable(),
    }),
  }),
  z.object({
    kind: z.literal('licence'),
    /** The participant's user id: review with `POST /admin/users/:id/licence/review`. */
    id: uuid,
    submittedAt: isoDate,
    participant,
    licence: z.object({ federation: z.string(), number: z.string() }),
  }),
]);
export type AdminReviewItem = z.infer<typeof adminReviewItem>;

export const adminReviewQueue = z.object({
  items: z.array(adminReviewItem),
  /** Whether the "document refused" email is configured (`events.brevo_template_document_rejected`). */
  rejectionEmail: z.boolean(),
});
export type AdminReviewQueue = z.infer<typeof adminReviewQueue>;
